import { PdfWatermarkForm } from '@today/ui-domain'
import { type PdfWatermarkSaveError, usePdfWatermarkSettings } from '@/hooks/usePdfWatermarkSettings'
import type { TenantConfigSectionProps } from '../section-props'

/** The message a `details.field` refusal puts next to its field (TO-495 Business rules). */
const FIELD_MESSAGES: Readonly<Record<string, string>> = {
	mode: 'Choose Disabled, Text or Image.',
	'text.template': 'Use 1–200 characters, no line breaks, and only the listed tokens.',
	'text.diagonalStamp': 'Turn the diagonal stamp on or off.',
	'image.mimeType': 'The image must be a PNG or a JPEG.',
	'image.contentBase64': 'The image must be a PNG or JPEG of 256 KB or smaller.',
	'image.opacity': 'Enter an opacity from 0.05 to 1.',
	'image.position': 'Choose a position.',
}

const fieldErrorsOf = (error: PdfWatermarkSaveError | null): Record<string, string> => {
	if (error?.kind !== 'invalid') return {}
	if (error.token !== undefined) return { [error.field]: `${error.token} is not a supported token.` }
	return { [error.field]: FIELD_MESSAGES[error.field] ?? 'Not valid.' }
}

const formErrorOf = (error: PdfWatermarkSaveError | null): string | null => {
	if (error?.kind === 'invalid') return 'Some fields need attention.'
	if (error?.kind === 'failed') return 'The watermark could not be saved. Try again.'
	return null
}

/**
 * The console's `pdf-watermark` section (TO-495 REQ-011): the tenant's
 * offer-PDF watermark. Both planes; the route follows the plane, and READ
 * access shows the settings without a save.
 */
export function PdfWatermarkSection({ tenantId, tenantSlug, plane, readOnly }: TenantConfigSectionProps) {
	const watermark = usePdfWatermarkSettings(tenantId, tenantSlug, plane)

	if (watermark.loadFailed) {
		return (
			<div role="alert" className="flex flex-col items-start gap-3 text-sm text-ds-danger">
				<p>Could not load the PDF watermark.</p>
				<button type="button" onClick={watermark.reload} className="rounded-md border px-3 py-1">
					Try again
				</button>
			</div>
		)
	}
	if (!watermark.settings) {
		return (
			<p className="text-sm text-ds-text-muted" aria-live="polite">
				Loading the PDF watermark…
			</p>
		)
	}

	const { settings } = watermark
	return (
		<PdfWatermarkForm
			key={settings.updatedAt ?? 'unconfigured'}
			settings={settings}
			readOnly={readOnly}
			isBusy={watermark.saving}
			fieldErrors={fieldErrorsOf(watermark.saveError)}
			error={formErrorOf(watermark.saveError)}
			onSave={watermark.save}
		/>
	)
}
